import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { CertLine } from "@/components/site/Cta";
import { BUSINESS, SERVICE_OPTIONS } from "@/content/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", phone: "", email: "", city: "", property_type: "Residential", service: "", message: "", website: "" };
const inputCls = "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3.5 text-[15px] text-white placeholder:text-zinc-500 outline-none transition-[border-color,background-color] duration-300 focus:border-amber-400/70 focus:bg-white/[0.05]";

const Field = ({ label, id, required, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-300">{label}{required && <span className="text-amber-400"> *</span>}</span>
    {children}
  </label>
);

const ContactAside = () => (
  <div className="lg:col-span-5">
    <Reveal className="flex items-center gap-3"><span className="h-px w-8 bg-amber-400/70" /><span className="eyebrow">Free Quote</span></Reveal>
    <MaskLines lines={["Let's talk about", "your project"]} className="mt-5 text-[1.7rem] leading-[1.12] sm:text-4xl lg:text-5xl font-semibold uppercase text-white" />
    <Reveal delay={0.1} as="p" className="mt-6 text-base md:text-lg leading-relaxed text-zinc-300">Tell us a little about your property and what you need. Our team will follow up to discuss next steps.</Reveal>
    <Reveal delay={0.15} className="mt-10 card-lux rounded-2xl p-7">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Call Solar Energy Doc LLC</p>
      <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="mt-3 flex items-center gap-3 font-display text-2xl sm:text-3xl text-white hover:text-amber-400 transition-colors">
        <Phone className="h-6 w-6 text-amber-400" />{BUSINESS.phoneDisplay}
      </a>
      <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-link" className="mt-4 flex items-center gap-3 text-sm text-zinc-300 hover:text-amber-400 transition-colors break-all">
        <Mail className="h-4 w-4 shrink-0 text-amber-400" />{BUSINESS.email}
      </a>
      <div className="mt-6 hairline" />
      <CertLine className="mt-6 !text-[10px] leading-relaxed" />
    </Reveal>
  </div>
);

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    const onQuote = (e) => { const s = e.detail?.service; if (s) setForm((f) => ({ ...f, service: s })); setDone(false); };
    window.addEventListener("sed:quote", onQuote);
    return () => window.removeEventListener("sed:quote", onQuote);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name.");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid phone number.");
    if (!form.service) return toast.error("Please select the service you need.");
    if (!API) return toast.error(`Online form unavailable. Please call ${BUSINESS.phoneDisplay}.`);
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, email: form.email.trim() || null });
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received. Our team will be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || `Something went wrong. Please call ${BUSINESS.phoneDisplay}.`);
    } finally { setSending(false); }
  };

  return (
    <section id="quote" data-testid="lead-form-section" className="relative overflow-hidden bg-[#08090a] py-24 sm:py-32">
      <div className="pointer-events-none absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <ContactAside />
        <Reveal className="lg:col-span-7">
          {done ? (
            <div data-testid="lead-form-success" className="glass flex min-h-[520px] flex-col items-center justify-center rounded-[28px] p-10 text-center">
              <CheckCircle2 className="h-14 w-14 text-amber-400" />
              <h3 className="mt-6 text-2xl font-semibold uppercase text-white">Thank you</h3>
              <p className="mt-3 max-w-sm text-zinc-300">Your request has been received. A member of our team will reach out shortly. Need help sooner? Call {BUSINESS.phoneDisplay}.</p>
              <button type="button" onClick={() => setDone(false)} data-testid="lead-form-new" className="btn btn-ghost mt-8">Submit another request</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="lead-form" className="glass rounded-[28px] p-6 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" id="lf-name" required><input id="lf-name" autoComplete="name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" data-testid="lead-form-name-input" /></Field>
                <Field label="Phone Number" id="lf-phone" required><input id="lf-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 555-5555" data-testid="lead-form-phone-input" /></Field>
                <Field label="Email" id="lf-email"><input id="lf-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="lead-form-email-input" /></Field>
                <Field label="City" id="lf-city"><input id="lf-city" autoComplete="address-level2" value={form.city} onChange={set("city")} className={inputCls} placeholder="Austin" data-testid="lead-form-city-input" /></Field>
              </div>
              <div className="mt-5">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-300">Property Type</span>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-white/12 bg-white/[0.03] p-1.5" role="radiogroup" aria-label="Property type">
                  {["Residential", "Commercial"].map((p) => (
                    <button key={p} type="button" role="radio" aria-checked={form.property_type === p} onClick={() => set("property_type")(p)} data-testid={`lead-form-property-${p.toLowerCase()}`}
                      className={`rounded-lg py-3 text-sm font-semibold transition-[background-color,color] duration-300 ${form.property_type === p ? "bg-amber-400 text-black" : "text-zinc-300 hover:text-white"}`}>{p}</button>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-300">What service do you need? <span className="text-amber-400">*</span></span>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="lead-form-service-select" className="h-auto rounded-xl border-white/12 bg-white/[0.03] px-4 py-3.5 text-[15px] text-white focus:ring-amber-400/50">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent data-lenis-prevent className="border-white/10 bg-[#14171c] text-white">
                    {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`lead-form-service-option-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-5">
                <Field label="Message" id="lf-msg"><textarea id="lf-msg" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your home, system or project…" data-testid="lead-form-message-input" /></Field>
              </div>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <button type="submit" disabled={sending} data-testid="lead-form-submit-button" className="btn btn-primary mt-8 w-full !py-5 disabled:opacity-70">
                {sending ? <Loader2 className="relative z-10 h-4 w-4 animate-spin" /> : null}
                <span className="relative z-10">{sending ? "Sending…" : "Request My Free Quote"}</span>
              </button>
              <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-zinc-400" data-testid="lead-form-privacy">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                Your information is kept private and only used to respond to your request. We never sell your data. No obligation.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
